import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  BellIcon,
  CalendarIcon,
  PencilIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/catalyst/button";
import { formatDate } from "@/lib/Formatters/formatDate";
import { WorkOrderJobOpeningItem } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningQuery";
import { JobOpeningStatusBadge } from "@/app/ProDrivers/WorkOrders/Components/JobOpeningStatusBadge";
import { useTranslations } from "use-intl";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { sendJobOpeningOfferNotificationsMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningMutations";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "@/components/catalyst/alert";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SendToDriversDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/SendToDriversDialog";
import { EditWorkOrderJobOpeningDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/EditWorkOrderJobOpeningDialog";
import { WorkOrderJobOpeningStatus } from "@/gql/graphql";
import { OffersKanban } from "@/app/ProDrivers/WorkOrders/Components/Cards/JobOpeningOfferViews";

interface WorkOrderJobOpeningCardProps {
  jobOpening: WorkOrderJobOpeningItem;
  jobNumber: number;
}

export function WorkOrderJobOpeningCard({
  jobOpening,
  jobNumber,
}: WorkOrderJobOpeningCardProps) {
  const t = useTranslations("workOrder.jobOpenings");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { open: openSendToDrivers } = useDialog(SendToDriversDialog);
  const { open: openEditDialog } = useDialog(EditWorkOrderJobOpeningDialog);

  const { mutateAsync: sendNotifications, isPending } = useGqlMutation(
    sendJobOpeningOfferNotificationsMutation,
  );

  const isClosed = jobOpening.status === WorkOrderJobOpeningStatus.Closed;

  async function handleSendNotifications() {
    await sendNotifications({ id: jobOpening.id });
    toast.success(t("sendNotifications.success"));
    setIsConfirmOpen(false);
  }

  return (
    <>
      <Card
        className={`overflow-hidden transition-shadow ${isClosed ? "opacity-60" : "hover:shadow-md"}`}
      >
        <div
          className={`border-l-4 ${isClosed ? "border-gray-300" : "border-green-500"}`}
        >
          <CardHeader className="border-b px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-gray-900">
                    {t("title")} {jobNumber}
                  </span>
                  <JobOpeningStatusBadge
                    id={jobOpening.id}
                    status={jobOpening.status}
                  />
                </div>

                {jobOpening.description ? (
                  <p className="text-sm leading-relaxed text-gray-600">
                    {jobOpening.description}
                  </p>
                ) : null}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5 text-xs text-gray-500">
                  {jobOpening.depart_at || jobOpening.deliver_at ? (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {jobOpening.depart_at
                        ? formatDate(jobOpening.depart_at)
                        : "—"}
                      {" → "}
                      {jobOpening.deliver_at
                        ? formatDate(jobOpening.deliver_at)
                        : "—"}
                    </span>
                  ) : null}
                  <span className="flex items-center gap-1">
                    <UsersIcon className="h-3.5 w-3.5" />
                    {jobOpening.positions_available}{" "}
                    {jobOpening.positions_available === 1
                      ? t("position")
                      : t("positions")}
                  </span>
                  <span>
                    {t("offersSent", { count: jobOpening.offers_count })}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      plain
                      onClick={() => openEditDialog({ jobOpening })}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("editDialog.title")}</TooltipContent>
                </Tooltip>
                {isClosed ? null : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button plain onClick={() => setIsConfirmOpen(true)}>
                        <BellIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("sendNotifications.button")}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <OffersKanban
              jobOpeningId={jobOpening.id}
              offersCount={jobOpening.offers_count}
              onInvite={
                isClosed
                  ? undefined
                  : () => openSendToDrivers({ jobOpeningId: jobOpening.id })
              }
            />
          </CardContent>
        </div>
      </Card>

      <Alert open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
        <AlertTitle>{t("sendNotifications.confirmTitle")}</AlertTitle>
        <AlertDescription>
          {t("sendNotifications.confirmMessage")}
        </AlertDescription>
        <AlertActions>
          <Button outline onClick={() => setIsConfirmOpen(false)}>
            {t("sendNotifications.cancel")}
          </Button>
          <Button
            color="blue"
            onClick={handleSendNotifications}
            disabled={isPending}
          >
            {isPending
              ? t("sendNotifications.sending")
              : t("sendNotifications.send")}
          </Button>
        </AlertActions>
      </Alert>
    </>
  );
}
